import { FC, useMemo, useState } from "react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MantineReactTable,
  useMantineReactTable,
  MRT_ColumnFiltersState,
  MRT_SortingState,
} from "mantine-react-table";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { Box, Button } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import { FileType } from "@funded-here-interface/common/src/constant/enum";
import { toast } from "react-toastify";
import useGetUpcData from "../../../../hooks/useGetUpcData";
import { UpcData } from "../../../../services/getUpcData";
import useDownloadUpcCSV from "../../../../hooks/useDownloadUpcCSV";

const Upc: FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const useDownloadUpcCSVMutation = useDownloadUpcCSV();

  const apiColumnFilters: any = JSON.stringify(
    columnFilters
      .filter((filter) => {
        if (Array.isArray(filter.value)) {
          return filter.value.every(
            (v) => v !== null && v !== undefined && v !== ""
          );
        }
        return (
          filter.value !== null &&
          filter.value !== undefined &&
          filter.value !== ""
        );
      })
      .map((filter) => {
        let column = filter.id;
        let value = filter.value;

        if (column === "Warehouse ID") {
          column = "warehouseId";
        }
        return {
          [column]: value,
        };
      })
  );

  const apiSorting: any = JSON.stringify(
    sorting.reduce((acc, sort) => {
      if (sort.id && sort.desc !== undefined) {
        acc[sort.id] = sort.desc;
      }
      return acc;
    }, {} as Record<string, any>)
  );

  const { data, error, isLoading } = useGetUpcData(
    token,
    pagination,
    apiColumnFilters,
    apiSorting
  );

  if (error) {
    toast.error((error as Error).message);
  }

  const totalRowCount = data?.meta?.totalRowCount ?? 0;

  const handleDownloadUpc = () => {
    useDownloadUpcCSVMutation.mutate(
      { token, apiColumnFilters, apiSorting },
      {
        onSuccess: (data: Blob) => {
          downloadFile(data, "Upc", FileType.CSV);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };
  const columns = useMemo<MRT_ColumnDef<UpcData>[]>(
    () => [
      {
        accessorKey: "upcId",
        header: "Upc ID",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "email",
        header: "Email",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "doiCalculated",
        header: "Doi Calculated",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? <span>true</span> : <div>false</div>,
      },
      {
        accessorKey: "quantity",
        header: "Quantity",
        filterVariant: "range",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "totalReturns",
        header: "Quantity Returned",
        filterVariant: "range",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "quantityInclusiveReturn",
        header: "Quantity Inclusive Return",
        filterVariant: "range",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "quantitySold",
        header: "Quantity Sold",
        filterVariant: "range",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "unitPrice",
        header: "Unit Price",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "location",
        header: "Location",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "productName",
        header: "Product Name",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "createdAt",
        header: "Created Date",
        filterVariant: "date-range",
      },
      {
        accessorFn: (row) => row.warehouse?.id,
        header: "Warehouse ID",
      },
      {
        accessorKey: "updatedAt",
        header: "Updated Date",
        filterVariant: "date-range",
      },
    ],
    []
  );

  const table = useMantineReactTable({
    enableColumnActions: true,
    enableColumnFilters: true,
    enableDensityToggle: false,
    enableMultiSort: true,
    paginationDisplayMode: "pages",
    state: { pagination, isLoading, sorting }, //pass the pagination and isLoading state to the table
    columns,
    data: data?.data ?? [],
    manualPagination: true,
    onPaginationChange: setPagination, //hoist pagination state to your state when it changes internally
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    rowCount: totalRowCount, //pass the total row count to the table
    initialState: { density: "xs" },
    mantineTableProps: {
      withColumnBorders: true,
      withBorder: true,
    },
    mantineTableHeadCellProps: {
      style: {
        padding: "0.5rem 1.0rem",
        color: "#23262a",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        padding: "0.5rem",
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <Button
          color="lightblue"
          //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
          onClick={handleDownloadUpc}
          leftIcon={<IconDownload />}
          variant="filled"
        >
          Export UPC
        </Button>
      </Box>
    ),
  });

  return (
    <div>
      <div>
        <MantineReactTable table={table} />
      </div>
    </div>
  );
};

export default Upc;
